import React, { useMemo, useCallback } from "react";
import { format } from "date-fns";
import { Button } from "@design-system/Button/Button";
import { RightArrowIcon } from "@icons";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    ReconciliationLogItem,
    ReconciliationActionType,
} from "./hooks/useReconciliationMatchingData";
import styles from "./GroupedHistoryDrawer.module.scss";

export interface ActionGroup {
    actionType: ReconciliationActionType;
    logs: ReconciliationLogItem[];
    description: string;
    date: Date;
    hasDetails: boolean;
}

interface GroupedHistoryLogListProps {
    logs: ReconciliationLogItem[];
    onExpandGroup?: (group: ActionGroup) => void;
}

const formatDate = (date: Date): string =>
    format(new Date(date), fullDateFullYearFormat);

const getGroupDescription = (
    actionType: ReconciliationActionType,
    logs: ReconciliationLogItem[],
): string => {
    const firstLog = logs[0];

    if (firstLog?.description) {
        return firstLog.description;
    }

    const count = firstLog?.count ?? logs.length;

    switch (actionType) {
        case ReconciliationActionType.STARTED:
            return "Started reconciliation";
        case ReconciliationActionType.COMPLETED:
            return "Completed reconciliation";
        case ReconciliationActionType.RECONCILED:
            return `Reconciled ${pluralizeWithCount("transaction", count)}`;
        case ReconciliationActionType.MATCHED:
            return `Matched ${pluralizeWithCount("transfer", count)}`;
        case ReconciliationActionType.UNMATCHED:
            return `Unmatched ${pluralizeWithCount("transfer", count)}`;
        case ReconciliationActionType.ADDED:
            return `Added ${pluralizeWithCount("transaction", count)}`;
        case ReconciliationActionType.REMOVED:
            return `Removed ${pluralizeWithCount("transaction", count)}`;
        default:
            return `${count} items`;
    }
};

interface GroupRowProps {
    group: ActionGroup;
    onExpand?: (group: ActionGroup) => void;
}

const GroupRow: React.FC<GroupRowProps> = ({ group, onExpand }) => {
    const handleExpand = useCallback(() => {
        onExpand?.(group);
    }, [group, onExpand]);

    return (
        <div className={styles.logRow}>
            <div className={styles.logRowDate}>
                <span className={styles.dateText}>
                    {formatDate(group.date)}
                </span>
            </div>
            <div className={styles.logRowContent}>
                <span className={styles.logDescription}>
                    {group.description}
                </span>
            </div>
            <div className={styles.logRowActions}>
                {group.hasDetails && (
                    <Button
                        variant="primary"
                        size="md"
                        iconOnly
                        onClick={handleExpand}
                        className={styles.viewButton}
                        title="View details"
                        aria-label="View details"
                    >
                        <RightArrowIcon />
                    </Button>
                )}
            </div>
        </div>
    );
};

export const GroupedHistoryLogList: React.FC<GroupedHistoryLogListProps> = ({
    logs,
    onExpandGroup,
}) => {
    const actionGroups = useMemo<ActionGroup[]>(() => {
        const groups: Record<string, ReconciliationLogItem[]> = {};

        logs.forEach((log) => {
            const key = log.actionType;
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(log);
        });

        const actionOrder: ReconciliationActionType[] = [
            ReconciliationActionType.STARTED,
            ReconciliationActionType.RECONCILED,
            ReconciliationActionType.MATCHED,
            ReconciliationActionType.ADDED,
            ReconciliationActionType.REMOVED,
            ReconciliationActionType.UNMATCHED,
            ReconciliationActionType.COMPLETED,
        ];

        return actionOrder
            .filter((actionType) => groups[actionType]?.length > 0)
            .map((actionType) => {
                const actionLogs = groups[actionType];
                const latestDate = actionLogs.reduce(
                    (latest, log) =>
                        new Date(log.date) > latest
                            ? new Date(log.date)
                            : latest,
                    new Date(actionLogs[0].date),
                );

                const hasDetails =
                    actionType !== ReconciliationActionType.STARTED &&
                    actionType !== ReconciliationActionType.COMPLETED;

                return {
                    actionType,
                    logs: actionLogs,
                    description: getGroupDescription(actionType, actionLogs),
                    date: latestDate,
                    hasDetails,
                };
            });
    }, [logs]);

    return (
        <div>
            <div className={styles.tableHeaderSpacer} />
            <div className={styles.tableHeader}>
                <div className={styles.headerDate}>Date</div>
                <div className={styles.headerAction}>Action</div>
                <div className={styles.headerActions} />
            </div>

            {actionGroups.map((group) => (
                <GroupRow
                    key={group.actionType}
                    group={group}
                    onExpand={onExpandGroup}
                />
            ))}
        </div>
    );
};
